import React, { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useDNSQueryStats, useDNSActivityData, useDNSThreatData } from "@/hooks/useDNSAnalytics";
import { useUserOrganization } from "@/hooks/useOrganization";
import { useOrganizationPolicies } from "@/hooks/useDNSPolicies";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3,
  TrendingUp,
  Shield,
  AlertTriangle,
  Download,
  Calendar,
  Filter,
  Eye,
  FileText,
  Users,
  Globe,
  Clock,
  Activity,
  PieChart,
  LineChart,
  TrendingDown,
  Zap
} from "lucide-react";

export default function Reports() {
  const { data: organization } = useUserOrganization();
  const [timeRange, setTimeRange] = useState('7d');

  const { data: stats, isLoading: statsLoading } = useDNSQueryStats(timeRange);
  const { data: activityData, isLoading: activityLoading } = useDNSActivityData(timeRange);
  const { data: threatData, isLoading: threatLoading } = useDNSThreatData(timeRange);
  const { data: policies = [], isLoading: policiesLoading } = useOrganizationPolicies();
  const [reportType, setReportType] = useState('overview');

  if (!organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Organization Required</h2>
          <p className="text-muted-foreground">
            Please set up your organization to view reports.
          </p>
        </div>
      </div>
    );
  }

  if (statsLoading || activityLoading || policiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Clock className="h-16 w-16 mx-auto mb-4 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  const getTimeRangeLabel = (range: string) => {
    switch (range) {
      case '24h': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      default: return 'Last 7 Days';
    }
  };

  const generateReport = () => {
    // TODO: Implement report generation
    console.log('Generating report for:', { timeRange, reportType });
  };

  const exportReport = () => {
    // TODO: Implement report export
    console.log('Exporting report for:', { timeRange, reportType });
  };

  return (
    <SidebarProvider>
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">DNS Security Reports</h1>
              <p className="text-muted-foreground">
                Comprehensive analytics and insights for {organization.name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-40">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={generateReport} variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
              <Button onClick={exportReport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Queries</CardTitle>
                <Globe className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.totalQueries?.toLocaleString() || '0'}</div>
                <p className="text-xs text-muted-foreground">
                  {getTimeRangeLabel(timeRange)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Blocked Queries</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {stats?.blockedQueries?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats?.totalQueries ?
                    `${((stats.blockedQueries / stats.totalQueries) * 100).toFixed(1)}% blocked` :
                    '0% blocked'
                  }
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Policies</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {policies.filter((p: any) => p.is_active).length}
                </div>
                <p className="text-xs text-muted-foreground">
                  {policies.length} total policies
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Threats Detected</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {stats?.threatsDetected?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {getTimeRangeLabel(timeRange)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs value={reportType} onValueChange={setReportType} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Activity Timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      DNS Activity Timeline
                    </CardTitle>
                    <CardDescription>
                      DNS queries and blocks over {getTimeRangeLabel(timeRange).toLowerCase()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {activityData && activityData.length > 0 ? (
                      <div className="space-y-3">
                        {activityData.slice(0, 10).map((activity: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                activity.action === 'blocked' ? 'bg-red-500' : 'bg-green-500'
                              }`} />
                              <div>
                                <p className="font-medium text-sm">{activity.domain}</p>
                                <p className="text-xs text-muted-foreground">
                                  {activity.category || 'Unknown category'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant={activity.action === 'blocked' ? 'destructive' : 'default'}>
                                {activity.action}
                              </Badge>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(activity.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No activity data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Policy Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Policy Summary
                    </CardTitle>
                    <CardDescription>
                      Current policy configuration and effectiveness
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Active Policies</span>
                        <Badge variant="default">
                          {policies.filter((p: any) => p.is_active).length}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Total Policies</span>
                        <Badge variant="outline">{policies.length}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Default Policy</span>
                        <Badge variant={policies.some((p: any) => p.is_default) ? 'default' : 'secondary'}>
                          {policies.some((p: any) => p.is_default) ? 'Active' : 'None'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Custom Domains</span>
                        <Badge variant="outline">
                          {policies.reduce((total: number, p: any) =>
                            total + (p.policy_domains?.length || 0), 0
                          )}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Threat Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Threat Categories
                    </CardTitle>
                    <CardDescription>
                      Breakdown of blocked content by category
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {threatLoading ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : Object.keys(threatData?.threatCounts ?? {}).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No blocked queries in this period.</p>
                    ) : (() => {
                      const counts = threatData!.threatCounts;
                      const max = Math.max(...Object.values(counts));
                      return (
                        <div className="space-y-3">
                          {Object.entries(counts)
                            .sort(([, a], [, b]) => b - a)
                            .map(([category, count]) => (
                              <div key={category} className="flex items-center justify-between">
                                <span className="text-sm capitalize">{category}</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-red-500 h-2 rounded-full"
                                      style={{ width: `${Math.round((count / max) * 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium w-8">{count}</span>
                                </div>
                              </div>
                            ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Top Blocked Domains */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Top Blocked Domains
                    </CardTitle>
                    <CardDescription>
                      Most frequently blocked domains
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {threatLoading ? (
                      <p className="text-sm text-muted-foreground">Loading…</p>
                    ) : (threatData?.topBlocked ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No blocked domains in this period.</p>
                    ) : (
                      <div className="space-y-3">
                        {(threatData!.topBlocked).map(({ domain, blocks }, index) => (
                          <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge variant="destructive" className="w-6 h-6 p-0 flex items-center justify-center">
                                {index + 1}
                              </Badge>
                              <span className="font-mono text-sm">{domain}</span>
                            </div>
                            <Badge variant="outline">
                              {blocks} blocks
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Response Times */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Response Times
                    </CardTitle>
                    <CardDescription>
                      DNS resolution performance metrics
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Average Response</span>
                        <Badge variant="default">12ms</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">95th Percentile</span>
                        <Badge variant="outline">45ms</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Slow Queries</span>
                        <Badge variant="secondary">2.3%</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Cache Hit Rate</span>
                        <Badge variant="default">87%</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Query Volume */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Query Volume
                    </CardTitle>
                    <CardDescription>
                      DNS query patterns and trends
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Peak Hour</span>
                        <Badge variant="default">2:00 PM</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Peak Volume</span>
                        <Badge variant="outline">1,247 queries</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Queries/Hour</span>
                        <Badge variant="secondary">~8,500</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Unique Domains</span>
                        <Badge variant="default">2,341</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Policy Compliance */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Policy Compliance
                    </CardTitle>
                    <CardDescription>
                      Compliance with security and content policies
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Content Filtering</span>
                        <Badge variant="default">100%</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Malware Protection</span>
                        <Badge variant="default">100%</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Adult Content</span>
                        <Badge variant="default">100%</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Gaming Sites</span>
                        <Badge variant="outline">95%</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Social Media</span>
                        <Badge variant="secondary">87%</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Audit Log */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Recent Policy Changes
                    </CardTitle>
                    <CardDescription>
                      Audit trail of policy modifications
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { action: 'Policy Created', policy: 'Malware Protection', user: 'admin@example.com', time: '2 hours ago' },
                        { action: 'Policy Updated', policy: 'Social Media Restriction', user: 'admin@example.com', time: '1 day ago' },
                        { action: 'Domain Blocked', policy: 'Custom Block', user: 'admin@example.com', time: '3 days ago' },
                        { action: 'Policy Deleted', policy: 'Old Gaming Policy', user: 'admin@example.com', time: '1 week ago' }
                      ].map((log, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                          <div>
                            <p className="text-sm font-medium">{log.action}</p>
                            <p className="text-xs text-muted-foreground">{log.policy}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{log.user}</p>
                            <p className="text-xs text-muted-foreground">{log.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
